import type { AnswerRecord, BingoPattern } from './types';

export const answerPointsWithStreakBonus = (
	points: number,
	streakEnabled: boolean,
	currentStreak: number,
	streakMultiplierStep = 1,
): number => {
	const award = Math.max(0, points);
	if (!streakEnabled) return award;
	return award * (1 + Math.max(0, currentStreak) * Math.max(1, streakMultiplierStep));
};

export const quizAward = (
	basePoints: number,
	correct: boolean,
	speedMode: boolean,
	remainingMs: number,
	durationMs: number,
	streakEnabled: boolean,
	currentStreak: number,
	streakMultiplierStep = 1,
): number => {
	if (!correct) return 0;
	let award = Math.min(1000, Math.max(0, basePoints));
	if (speedMode) {
		const duration = Math.max(1, durationMs);
		const remaining = Math.min(duration, Math.max(0, remainingMs));
		award = Math.round(award * remaining / duration);
	}
	return answerPointsWithStreakBonus(award, streakEnabled, currentStreak, streakMultiplierStep);
};

export interface BingoPatternMatch {
	key: string;
	indexes: number[];
}

export interface BingoScoreRules {
	patterns: BingoPattern[];
	correctAnswerPoints: number;
	bonusEnabled?: boolean;
	patternBonusPoints: number;
	fullBoardBonusEnabled: boolean;
	fullBoardBonusPoints: number;
	streakEnabled: boolean;
	streakMultiplierStep: number;
	useRecordedAnswerAwards: boolean;
}

export interface BingoScoreBreakdown {
	total: number;
	correctAnswers: number;
	completedPatterns: BingoPatternMatch[];
	answerPoints: number;
	streakBonus: number;
	patternBonus: number;
	fullBoardBonus: number;
	bonusPoints: number;
}

const defaultPatterns: BingoPattern[] = ['row', 'column', 'diagonal'];

export const bingoPatternLabel = (key: string): string => ({
	row1: 'top row',
	row2: 'middle row',
	row3: 'bottom row',
	column1: 'left column',
	column2: 'middle column',
	column3: 'right column',
	diagonal1: 'diagonal',
	diagonal2: 'diagonal',
	fourCorners: 'four corners',
	fullBoard: 'full board',
}[key] ?? 'winning pattern');

export const formatBingoBonusCopy = (copy: string, pattern: string, points: number): string =>
	copy
		.replaceAll('{pattern}', pattern)
		.replaceAll('{points}', String(points));

export const bingoPatternMatches = (
	markedIndexes: number[],
	patterns: BingoPattern[] = defaultPatterns,
): BingoPatternMatch[] => {
	const marked = new Set(markedIndexes);
	const enabled = new Set(patterns.length ? patterns : defaultPatterns);
	const candidates: Array<BingoPatternMatch & { pattern: BingoPattern }> = [
		{ key: 'row1', pattern: 'row', indexes: [0, 1, 2] },
		{ key: 'row2', pattern: 'row', indexes: [3, 4, 5] },
		{ key: 'row3', pattern: 'row', indexes: [6, 7, 8] },
		{ key: 'column1', pattern: 'column', indexes: [0, 3, 6] },
		{ key: 'column2', pattern: 'column', indexes: [1, 4, 7] },
		{ key: 'column3', pattern: 'column', indexes: [2, 5, 8] },
		{ key: 'diagonal1', pattern: 'diagonal', indexes: [0, 4, 8] },
		{ key: 'diagonal2', pattern: 'diagonal', indexes: [2, 4, 6] },
		{ key: 'fourCorners', pattern: 'fourCorners', indexes: [0, 2, 6, 8] },
		{ key: 'fullBoard', pattern: 'fullBoard', indexes: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
	];
	return candidates
		.filter((candidate) => enabled.has(candidate.pattern) && candidate.indexes.every((index) => marked.has(index)))
		.map(({ key, indexes }) => ({ key, indexes }));
};

export const bingoLines = (markedIndexes: number[], patterns: BingoPattern[] = defaultPatterns): number[][] =>
	bingoPatternMatches(markedIndexes, patterns)
		.filter((match) => match.key.startsWith('row') || match.key.startsWith('column') || match.key.startsWith('diagonal'))
		.map((match) => match.indexes);

export const bingoScoreBreakdown = (
	answers: AnswerRecord[],
	rules: Partial<BingoScoreRules> = {},
): BingoScoreBreakdown => {
	const marked = [...new Set(answers.filter((answer) => answer.correct).map((answer) => answer.boardIndex ?? -1))]
		.filter((index) => index >= 0 && index < 9);
	const completedPatterns = bingoPatternMatches(marked, rules.patterns ?? defaultPatterns);
	const correctAnswerPoints = Math.min(10000, Math.max(0, rules.correctAnswerPoints ?? 10));
	const bonusEnabled = rules.bonusEnabled ?? true;
	const patternBonusPoints = bonusEnabled ? Math.min(10000, Math.max(0, rules.patternBonusPoints ?? 50)) : 0;
	const fullBoardBonusPoints = bonusEnabled ? Math.min(10000, Math.max(0, rules.fullBoardBonusPoints ?? 100)) : 0;
	let currentStreak = 0;
	const calculatedAnswerPoints = answers.reduce((total, answer) => {
		if (!answer.correct) {
			currentStreak = 0;
			return total;
		}
		const award = answerPointsWithStreakBonus(
			correctAnswerPoints,
			rules.streakEnabled ?? false,
			currentStreak,
			rules.streakMultiplierStep ?? 1,
		);
		currentStreak++;
		return total + award;
	}, 0);
	const answerPoints = rules.useRecordedAnswerAwards
		? answers.reduce((total, answer) => total + (answer.correct ? Math.max(0, answer.award) : 0), 0)
		: calculatedAnswerPoints;
	const streakBonus = Math.max(0, answerPoints - marked.length * correctAnswerPoints);
	const patternBonus = completedPatterns.length * patternBonusPoints;
	const fullBoardBonus = marked.length === 9
		&& (rules.patterns ?? defaultPatterns).includes('fullBoard')
		&& (rules.fullBoardBonusEnabled ?? false)
		? fullBoardBonusPoints
		: 0;
	return {
		total: answerPoints + patternBonus + fullBoardBonus,
		correctAnswers: marked.length,
		completedPatterns,
		answerPoints,
		streakBonus,
		patternBonus,
		fullBoardBonus,
		bonusPoints: streakBonus + patternBonus + fullBoardBonus,
	};
};

export const bingoScore = (answers: AnswerRecord[], rules: Partial<BingoScoreRules> = {}): number =>
	bingoScoreBreakdown(answers, rules).total;
